import type JCE from '@chesscom/js-chess-engine';
import type { Callback } from 'utils/map/tagged-map';
import type { API } from 'chessboard/api/config/types';
import type { RawAnalysisHighlight } from 'chessboard/plugins/highlights/modules/analysis/types';
import type { RawArrow } from 'chessboard/plugins/arrows/config/types';
import type { KeyModifiers } from 'chessboard/model/dom/types';

export namespace Markings {
  export namespace Plugin {
    export const Name = 'markings';
  }

  export interface API {
    addMany(newMarkings: (Markings.Model | Markings.KeyedModel)[]): (string | undefined | null)[];
    addOne(newMarking: Markings.Model | Markings.KeyedModel): string | undefined | null;
    getAll(): Markings.KeyedModel[];
    getAllWhere(options?: Markings.WhereOptions): Markings.KeyedModel[];
    getMany(keys: string[]): Markings.KeyedModel[];
    getOne(key: string): Markings.KeyedModel | undefined;
    removeAll(): boolean;
    removeAllWhere(options?: Markings.WhereOptions): boolean;
    removeMany(keysOrMarks: (Markings.KeyedModel | string)[]): boolean;
    removeOne(keyOrMark: string | Markings.KeyedModel, ids?: NodeIds | number): boolean;
    toggleMany(
      markings: (Markings.Model | Markings.KeyedModel)[],
    ): (ReturnType<API['removeOne']> | ReturnType<API['addOne']>)[];
    toggleOne(
      marking: Markings.Model | Markings.KeyedModel,
    ): ReturnType<API['removeOne']> | ReturnType<API['addOne']>;
    factory: MarkingFactoryMethods;
  }

  export interface MarkingFactoryMethods {
    buildStandardAnalysisHighlight(
      square: JCE.SanSquare,
      keyPressed?: KeyModifiers,
    ): RawAnalysisHighlight;
    buildStandardArrow(from: JCE.SanSquare, to: JCE.SanSquare, keyPressed?: KeyModifiers): RawArrow;
  }

  export interface Params {
    context?: API.Context;
    generateKey?: (marking: Markings.Model) => string;
    onMarkingAdded?: Callback<Markings.KeyedModel | Markings.KeyedModel[]>;
    onMarkingRemoved?: Callback<Markings.KeyedModel | Markings.KeyedModel[]>;
    onMarkingAddedToAlternateNode?: Callback<Markings.KeyedModel | Markings.KeyedModel[]>;
    onMarkingRemovedFromAlternateNode?: Callback<Markings.KeyedModel | Markings.KeyedModel[]>;
  }

  export interface PublicAPI {
    [Markings.Plugin.Name]: Markings.API;
  }

  export interface WhereOptions {
    node?: boolean;
    persistent?: boolean;
    tags?: string[];
    types?: string[];
  }

  export type Data = { [key: string]: any };

  export interface Model {
    data: Data;
    node?: boolean | NodeIds;
    persistent?: boolean;
    tags?: string[];
    type: string;
  }

  export interface NodeIds {
    line: number;
    move: number;
  }

  export type KeyedModel = Model & {
    key: string;
    id?: string;
  };

  // Utility types
  export type WithColor = {
    color: string;
  };

  export type WithFromTo = {
    from: JCE.SanSquare;
    to: JCE.SanSquare;
  };

  export type WithOpacity = {
    opacity: number;
  };

  export type WithSquare = {
    square: JCE.SanSquare;
  };
}

export type ParsedCommandChanges = {
  operation: 'add' | 'remove';
  command: Markings.KeyedModel;
};

export type ModifyJCEMarkingResult = {
  changes: ParsedCommandChanges;
  ids: JCE.Ids;
};
